import React, { useState, useEffect } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { useTranslation } from "react-i18next";
import api from "../../../api/axios";
import { companyID } from "../../../auth";
import { formatDateInTimezone } from "../../../utils/formatDateInTimezone";
import { Table } from "antd";
import PlanConfigurationModal from "../../../components/PlanConfigurationModal";

const CompanySubscriptionDetails = () => {
  const { t } = useTranslation();
  const [companySubscriptionsDetails, setCompanySubscriptionsDetails] =
    useState([]);
  const [plans, setPlans] = useState([]);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [selectedPlanForRenewal, setSelectedPlanForRenewal] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);

  const fetchCompanySubscriptionDetails = async () => {
    const company_id = companyID();
    try {
      const response = await api.get(
        `subscriptions/subscription-details?company_id=${company_id}`
      );
      setCompanySubscriptionsDetails(response?.data?.data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get("plans");
      if (response.status === 200) {
        setPlans(response.data.data ?? []);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  useEffect(() => {
    fetchCompanySubscriptionDetails();
    fetchPlans();
  }, []);

  const isExpired = (endDate) => {
    if (!endDate) return false;
    const today = new Date();
    const subscriptionEndDate = new Date(endDate);

    // Calculate the date 5 days from now
    const fiveDaysFromNow = new Date();
    fiveDaysFromNow.setDate(today.getDate() + 5);

    return (
      subscriptionEndDate < today || subscriptionEndDate <= fiveDaysFromNow
    );
  };

  const handleRenew = (record) => {
    // Find the plan that matches the current subscription
    const currentPlan = plans.find((p) => p.plan_name === record.plan_name);
    if (currentPlan) {
      setPaymentMode("renewal");
      setSelectedPlanForRenewal(currentPlan.plan_id);
      setCurrentSubscription(record);
      setShowRenewModal(true);
    } else {
      console.error("Plan not found for renewal:", record.plan_name);
      if (plans.length > 0) {
        setSelectedPlanForRenewal(plans[0].plan_id);
        setCurrentSubscription(record);
        setShowRenewModal(true);
      }
    }
  };

  const handlePay = (record) => {
    const currentPlan = plans.find((p) => p.plan_name === record.plan_name);

    if (currentPlan) {
      // Check user's subscription count
      const totalSubscriptions = companySubscriptionsDetails.length;
      // Check if the only subscription has plan_id = 1
      const onlySubscription =
        totalSubscriptions === 2 &&
        companySubscriptionsDetails[0].plan_id === 1;

      if (onlySubscription) {
        // Only one plan and it's Free → set due
        setPaymentMode("due");
      } else {
        // Otherwise → renewal
        setPaymentMode("renewal");
      }

      console.log(currentPlan, "current plan in pay");

      setSelectedPlanForRenewal(currentPlan.plan_id);
      setCurrentSubscription(record);
      setShowRenewModal(true);
      console.log(record, currentPlan.paymentMode, "renew/ due mode");
    } else {
      console.error("Plan not found for renewal:", record.plan_name);

      if (plans.length > 0) {
        setSelectedPlanForRenewal(plans[0].plan_id);
        setCurrentSubscription(record);
        setShowRenewModal(true);
      }
    }
  };

  const handleRenewalSuccess = () => {
    setShowRenewModal(false);
    setSelectedPlanForRenewal(null);
    setCurrentSubscription(null);
    // Refresh subscription details
    fetchCompanySubscriptionDetails();
  };

  const columns = [
    {
      title: t("Plan Name"),
      dataIndex: "plan_name",
    },
    {
      title: t("Total Persons"),
      dataIndex: "total_persons",
    },
    {
      title: t("Start Date"),
      dataIndex: "start_date",
      render: (text) => (text ? formatDateInTimezone(text) : "-"),
    },
    {
      title: t("Expiry Date"),
      dataIndex: "end_date",
      render: (text) => (text ? formatDateInTimezone(text) : "-"),
    },
    {
      title: t("Merchant Order ID"),
      dataIndex: "merchant_order_id",
      render: (text) => text || "-",
    },
    {
      title: t("Subscription Status"),
      dataIndex: "subscription_status",
      render: (text) => {
        let badgeClass = "secondary";
        let displayText = "-";
        if (text) {
          displayText =
            text.charAt(0).toUpperCase() + text.slice(1).replace(/_/g, " ");
          if (text === "active") badgeClass = "success";
          else if (text === "payment_pending") badgeClass = "warning";
          else if (text === "expired") badgeClass = "danger";
        }
        return <span className={`badge bg-${badgeClass}`}>{displayText}</span>;
      },
    },
    {
      title: t("Action"),
      dataIndex: "action",
      render: (text, record) => {
        const { subscription_status, end_date } = record;
        const expired = isExpired(end_date);

        if (record?.is_renewed) {
          return "-";
        }

        if (subscription_status === "active" && expired) {
          return (
            <button
              className="btn btn-outline-warning btn-sm"
              onClick={() => handleRenew(record)}
              title={t("Complete payment to activate your subscription")}
            >
              <i className="fa fa-credit-card" style={{ marginRight: 4 }} />
              {t("Renew")}
            </button>
          );
        }

        if (subscription_status === "payment_pending") {
          return (
            <button
              className="btn btn-outline-warning btn-sm"
              onClick={() => handlePay(record)}
              title={t("Complete payment to activate your subscription")}
            >
              <i className="fa fa-credit-card" style={{ marginRight: 4 }} />
              {t("Pay Now")}
            </button>
          );
        }

        return "-";
      },
    },
  ];

  return (
    <div>
      <div className="page-wrapper" style={{ position: "relative" }}>
        {/* Page Content */}
        <div className="content container-fluid">
          {/* Page Header */}
          <Breadcrumbs
            maintitle={t("Subscription Detail")}
            title={t("Dashboard")}
            subtitle={t("Subscription Detail")}
            name={t("Add Employee")}
            page="employees"
            action="create"
          />

          <div className="row">
            <div className="col-md-12">
              <div className="table-responsive">
                <Table
                  className="table-striped"
                  columns={columns}
                  dataSource={companySubscriptionsDetails}
                  rowKey="subscription_id"
                  scroll={{ x: true }}
                  tableLayout="auto"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Renewal Modal */}
      <PlanConfigurationModal
        show={showRenewModal}
        onHide={() => {
          setShowRenewModal(false);
          setSelectedPlanForRenewal(null);
          setCurrentSubscription(null);
        }}
        plans={plans}
        selectedPlanId={selectedPlanForRenewal}
        onPaymentSuccess={handleRenewalSuccess}
        modalType={paymentMode}
        currentPlan={currentSubscription}
      />
    </div>
  );
};

export default CompanySubscriptionDetails;
